import '@beyondtrust/btux-iconography-web';
import { localized, msg } from '@lit/localize';
import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import urlJoin from 'url-join';
import { ProductAPIMap } from '../../constants';
import { DirtyFormController } from '../../controllers/dirty-form-controller';
import { FlagSet } from '../../feature-flags';
import { t } from '../../services/runtime-i18n';
import { APIFeatureDefinition, APIMenuDefinition, APIMenuItem, APITagItem, ProductMenuItem } from '../../types';
import { NAV_SECTION_TAG } from '../nav-section/nav-section';
import '../tag/tag';
import styles from './menu-sidebar-panel.styles.scss';

export const MENU_SIDEBAR_PANEL_TAG = 'platform-menu-sidebar-panel' as const;

const renderedLinks = (el: Element): HTMLAnchorElement[] => {
  if (el instanceof HTMLSlotElement) {
    return el.assignedElements({ flatten: true }).flatMap(renderedLinks);
  }
  const self = el.matches('a[href]') ? [el as HTMLAnchorElement] : [];
  return [...self, ...[...(el.shadowRoot ?? el).children].flatMap(renderedLinks)];
};

/**
 * Second-level navigation panel — the sidebar's analog of `platform-api-menu`.
 * Renders a product's API-driven menu with feature sets as accordion panels
 * (sl-details, all open initially); features without items render as flat link
 * rows. Presentational: the sidebar owns open/pinned state and data fetching,
 * and listens for the `pin-toggle` event this fires.
 */
@customElement(MENU_SIDEBAR_PANEL_TAG)
@localized()
export class PlatformMenuSidebarPanel extends LitElement {
  static override styles = [styles];

  @property({ attribute: false })
  product?: ProductMenuItem;

  /** Panel title for non-product panels (e.g. Reporting); pass it pre-localized. */
  @property()
  heading = '';

  @property({ attribute: false })
  menu?: APIMenuDefinition;

  @property({ attribute: false })
  flags: FlagSet = {};

  @property({ type: Boolean })
  pinned = false;

  /**
   * The consumer supplied replacement menu content (slotted); render it instead
   * of the API-driven features — same precedence the overlay menu gives its
   * per-product slots.
   */
  @property({ type: Boolean })
  consumerMenu = false;

  /** The menu fetch failed: show an error instead of loading forever. */
  @property({ type: Boolean })
  menuError = false;

  // Inert until a dirty-form context provider exists in this tree; keeps link
  // handling ready to match platform-api-menu once the sidebar grows one.
  dirtyFormController = new DirtyFormController(this);

  override render() {
    // Default to the site root so static-menu links keep their leading slash
    // (urlJoin('', '/x') would yield the relative 'x').
    const baseURL = this.product ? (ProductAPIMap[this.product.slug]?.baseURL ?? '/') : '/';
    const features = this.menu?.features?.filter(
      (feat) => !feat.featureFlag || (this.flags[feat.featureFlag] ?? false),
    );

    return html`
      <header>
        <h2>${this.product ? t(this.product.slug, this.product.name) : this.heading}</h2>
        <button
          type="button"
          aria-pressed=${this.pinned}
          aria-label=${msg('Pin navigation open', {
            desc: 'Keeps the second-level navigation panel open',
          })}
          @click=${() => this.dispatchEvent(new Event('pin-toggle', { bubbles: true, composed: true }))}
        >
          ${keyed(
            this.pinned,
            // The icon shows the action, not the state: an unpinned panel offers
            // the pin. (btux-svg only reads its name at first render, so
            // re-create it per state.)
            html`<btux-svg name=${this.pinned ? 'menu-unpin' : 'menu-pin'}></btux-svg>`,
          )}
        </button>
      </header>
      ${this.consumerMenu || !this.product
        ? html`<slot></slot>`
        : features
          ? repeat(
              features,
              // id is optional in the API type: fall back to the position so
              // two id-less features never share a key.
              (feat, index) => `${this.product?.slug}:${feat.id ?? index}`,
              (feat) => this.renderFeature(feat, baseURL),
            )
          : this.menuError
            ? html`<p>${msg('Unable to load the menu. Close and reopen it to try again.')}</p>`
            : html`<p>${msg('Loading...')}</p>`}
    `;
  }

  /**
   * Focus the first nav link a keyboard user should land on, returning whether
   * focus actually landed there. Callers must respect `false` — a still-loading,
   * errored, empty, or fully-collapsed panel has nothing to focus, and moving
   * focus anyway would strand the user on a dead element.
   *
   * The return value reports the outcome, not just whether a candidate link was
   * found: a link can exist in this shadow root yet still be unfocusable,
   * because its owning `platform-nav-section` hasn't rendered its `sl-details`
   * (and therefore the `<slot>` the link needs to be assigned to) yet. Calling
   * `.focus()` on such a link is a silent no-op, and reporting success there
   * would let a caller believe the user landed in the panel while focus is
   * still on the trigger that opened it.
   *
   * @public
   */
  focusFirstItem(): boolean {
    const first = this.navLinks().find((link) => {
      // Collapsed sections must be skipped, not just avoided: focusing a link
      // inside one wouldn't actually move focus, yet returning true here would
      // tell the caller it did — stranding the keyboard user on the trigger
      // with no signal anything failed. `open` is platform-nav-section's own
      // reflected public state, and the links live in this shadow root —
      // slotted *into* the section — so closest() reaches it without crossing
      // a shadow boundary.
      const section = link.closest(NAV_SECTION_TAG);
      return section == null || section.open;
    });
    if (first == undefined) {
      return false;
    }
    first.focus();
    // getRootNode() reaches whichever root the link actually lives in -- this
    // shadow root for internal, API-driven links, or the consumer's own
    // document (or shadow root, if the panel itself is nested inside one) for
    // a slotted consumer anchor -- so the check holds regardless of shadow
    // depth, unlike comparing against this.shadowRoot / document directly.
    return (first.getRootNode() as unknown as DocumentOrShadowRoot).activeElement === first;
  }

  /**
   * Resolves once this panel and its nested accordion machinery have rendered, so
   * a caller can focus content that is genuinely in the flattened tree.
   *
   * Lit's `updateComplete` does not await child element updates, and a child
   * finishing its render never triggers another update on an ancestor — so a
   * caller driven by the sidebar's update cycle alone can attempt focus before the
   * anchors are assignable to a slot.
   *
   * @public
   */
  async whenContentSettled(): Promise<void> {
    await this.updateComplete;
    const sections = [...(this.shadowRoot?.querySelectorAll(NAV_SECTION_TAG) ?? [])];
    await Promise.all(sections.map((section) => section.updateComplete));
    await Promise.all(
      sections.map(async (section) => {
        // Guard both existence (the section may not have rendered its
        // sl-details child yet on a first pass) and upgrade (it may not yet be
        // the real custom element instance).
        const details = section.shadowRoot?.querySelector('sl-details');
        if (details && 'updateComplete' in details) {
          await details.updateComplete;
        }
      }),
    );
  }

  /** Nav links in render order, from whichever content mode is active. */
  private navLinks(): HTMLAnchorElement[] {
    if (this.consumerMenu || !this.product) {
      const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])');
      // flatten: true is required — the sidebar forwards consumer content
      // through a nested <slot name="...">, so unflattened assignment stops at
      // that slot instead of reaching the real anchors.
      const assigned = slot?.assignedElements({ flatten: true }) ?? [];
      return assigned.flatMap(renderedLinks);
    }
    return [...(this.shadowRoot?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? [])];
  }

  private renderFeature(feat: APIFeatureDefinition, baseURL: string): TemplateResult<1> | typeof nothing {
    const items = feat.items?.filter((item) => !item.featureFlag || (this.flags[item.featureFlag] ?? false));

    if (items == undefined || items.length === 0) {
      // Nameless with nothing to list: render nothing (overlay parity) — a flat
      // row would otherwise have no accessible name.
      if (feat.name == undefined) {
        return nothing;
      }
      const title = html`
        ${feat.icon
          ? html`<btux-svg name=${feat.icon}></btux-svg>`
          : // Reserve the icon footprint so the text joins the panel's single
            // column either way, like platform-nav-section headings.
            html`<span class="icon-spacer"></span>`}
        <span>${t(feat.id, feat.name ?? '')} ${this.renderTags(feat.tags)}</span>
      `;
      // Link-only feature (e.g. "Dashboard"): a flat row, not an accordion.
      if (feat.link == undefined) {
        return html`<div class="feature-link">${title}</div>`;
      }
      const href = feat.absolutePath ? urlJoin('/', feat.link) : urlJoin(baseURL, feat.link);
      return html`
        <a class="feature-link" aria-current=${ifDefined(this.isCurrentPath(href) ? 'page' : undefined)} href=${href}>
          ${title}
        </a>
      `;
    }

    const renderMenuItem = (item: APIMenuItem) => {
      const href = item.absolutePath ? urlJoin('/', item.link) : urlJoin(baseURL, item.link);
      return html`
        <li>
          <a
            aria-current=${ifDefined(this.isCurrentPath(href) ? 'page' : undefined)}
            data-bt="${'navItem-' + item.id}"
            target=${item.openExternal ? '_blank' : '_self'}
            rel=${ifDefined(item.openExternal ? 'noopener noreferrer' : undefined)}
            href=${href}
          >
            ${t(item.id, item.name)} ${this.renderTags(item.tags)}
          </a>
        </li>
      `;
    };

    // A nameless feature has no accordion to belong to (matching the overlay's
    // api-menu, e.g. Entitle's first group): its items are bare top-level links.
    if (feat.name == undefined) {
      return html`<menu class="bare">${items.map(renderMenuItem)}</menu>`;
    }

    return html`
      <platform-nav-section open icon=${ifDefined(feat.icon || undefined)}>
        <span slot="heading">${t(feat.id, feat.name ?? '')} ${this.renderTags(feat.tags)}</span>
        <menu>${items.map(renderMenuItem)}</menu>
      </platform-nav-section>
    `;
  }

  /** Whether a link points at the page the user is on (query strings ignored). */
  private isCurrentPath(href: string): boolean {
    const linkPath = href.split(/[?#]/)[0].replace(/\/+$/, '');
    const currentPath = window.location.pathname.replace(/\/+$/, '');
    return linkPath === currentPath;
  }

  private renderTags(tags?: APITagItem[]): TemplateResult<1> | typeof nothing {
    if (tags == undefined) {
      return nothing;
    }
    return html`${tags.map(
      (tag) =>
        html`${choose(
          tag.name,
          [
            ['new', () => html`<platform-tag variant="new">${msg('New')}</platform-tag>`],
            ['preview', () => html`<platform-tag variant="preview">${msg('Preview')}</platform-tag>`],
            ['early access', () => html`<platform-tag variant="early-access">${msg('Early Access')}</platform-tag>`],
          ],
          () => html``,
        )}`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [MENU_SIDEBAR_PANEL_TAG]: PlatformMenuSidebarPanel;
  }
}
